import React, { useState } from 'react';
import { TrendingUp, DollarSign, Users, ShoppingBag, Package, BarChart3, Share2, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function MarketUS() {
  const [showShareMenu, setShowShareMenu] = useState(false);

  const generatePDF = async () => {
    const content = document.getElementById('market-report-content');
    if (!content) return;

    const canvas = await html2canvas(content, {
      scale: 2,
      useCORS: true,
      logging: false
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pageHeight = 297;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save('US-Market-Report-Motif Unbound.pdf');
  };

  const shareReport = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent('Check out this US Market Report from Motif Unbound');
    
    const shareUrls = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <div id="market-report-content" className="max-w-6xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold mb-6 text-[#000000]">
          United States Market Overview
        </h1>
        
        <p className="text-xl text-gray-700 mb-12">
          The world's largest retail market with unparalleled opportunities for DTC brands and manufacturers.
        </p>

        <section className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] text-white p-6 rounded-xl">
            <DollarSign size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">$7.24T</div>
            <div className="text-sm opacity-90">Total Retail Sales 2025</div>
          </div>
          <div className="bg-gradient-to-br from-[#FFD965] to-[#f5cc4d] text-[#000000] p-6 rounded-xl">
            <TrendingUp size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">+3.2%</div>
            <div className="text-sm opacity-90">YoY Growth Rate</div>
          </div>
          <div className="bg-gradient-to-br from-[#38A3A5] to-[#000000] text-white p-6 rounded-xl">
            <ShoppingBag size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">$1.47T</div>
            <div className="text-sm opacity-90">E-commerce Sales 2025</div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">2025 Retail Performance Highlights</h2>
          <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">E-commerce Dominance</h3>
                  <p className="text-gray-700 mb-2">E-commerce represents 20.3% of total retail sales in 2025, up from 19.1% in 2024. Online grocery and direct-to-consumer brands are driving the growth.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>Amazon maintains 38% of e-commerce market share</li>
                    <li>DTC brands grew 18% year-over-year</li>
                    <li>Mobile commerce accounts for 56% of online sales</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFD965] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users size={24} className="text-[#000000]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Consumer Behavior Shifts</h3>
                  <p className="text-gray-700 mb-2">American consumers are increasingly value-conscious while seeking premium, sustainable products.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>73% of consumers research products online before purchasing</li>
                    <li>Sustainability influences 64% of purchase decisions</li>
                    <li>Social commerce grew 25% in 2025</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Package size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Supply Chain Evolution</h3>
                  <p className="text-gray-700 mb-2">Nearshoring and reshoring trends continue as brands prioritize supply chain resilience.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>Mexico manufacturing partnerships increased 34%</li>
                    <li>Average delivery times reduced to 2.1 days</li>
                    <li>Same-day delivery available in 150+ metro areas</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Key Retail Categories in 2025</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Health & Wellness</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$698B</div>
              <p className="text-gray-700 text-sm mb-3">Leading growth category with 8.2% annual growth</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Supplements and vitamins</li>
                <li>• Fitness equipment and wearables</li>
                <li>• Natural and organic products</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Home & Garden</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$512B</div>
              <p className="text-gray-700 text-sm mb-3">Sustained growth from remote work trends</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Smart home devices</li>
                <li>• Furniture and decor</li>
                <li>• Outdoor living products</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Fashion & Apparel</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$486B</div>
              <p className="text-gray-700 text-sm mb-3">Athleisure and sustainable fashion dominate</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Athletic and activewear</li>
                <li>• Sustainable and ethical brands</li>
                <li>• Direct-to-consumer footwear</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Electronics & Tech</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$461B</div>
              <p className="text-gray-700 text-sm mb-3">AI and smart devices driving innovation</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• AI-powered devices</li>
                <li>• Consumer electronics</li>
                <li>• Gaming and entertainment</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-r from-[#38A3A5] to-[#38A3A5] text-white p-8 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">Ready to Enter the US Market?</h2>
          <p className="mb-6">We provide end-to-end support for foreign brands entering the United States, from market entry strategy to supply chain setup and sales acceleration.</p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
              className="bg-[#FFD965] hover:bg-[#f5cc4d] text-[#000000] px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
            >
              Schedule a Consult
            </a>
            <button 
              onClick={generatePDF}
              className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
            >
              <Download size={20} />
              Download Market Report
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
              >
                <Share2 size={20} />
                Share Report
              </button>
              {showShareMenu && (
                <div className="absolute top-full mt-2 bg-white rounded-lg shadow-xl p-3 min-w-[200px] z-10">
                  <button
                    onClick={() => shareReport('whatsapp')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    📱 WhatsApp
                  </button>
                  <button
                    onClick={() => shareReport('linkedin')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    💼 LinkedIn
                  </button>
                  <button
                    onClick={() => shareReport('twitter')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    🐦 Twitter
                  </button>
                  <button
                    onClick={() => shareReport('facebook')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    👥 Facebook
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default MarketUS;
